import React, { useCallback, useMemo } from 'react';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { IconAddCircleOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconAddCircleOutline';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { IconChevronRightSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronRight';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { IconCheckMark24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconCheckMark24px';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { IconEditSvg } from 'apps/sigma2-ui/src/components/svg/IconEdit';
import { IconTrashCanSvg } from 'apps/sigma2-ui/src/components/svg/IconTrashCan';
import { IconInfinitySymbolSvg } from 'apps/sigma2-ui/src/components/svg/IconInfinitySymbol';
import { compileDataRulePattern } from 'libs/end-to-end/src/data-rules';
import { DataRulePattern } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentText } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWildcardString } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWildcardStringExcludingModes } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWildcardStringExcludingMode } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWildcardNumber } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWildcardDate } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentWhitespace } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegment } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentType } from 'libs/end-to-end/src/data-rules';
import { DataRulePatternSegmentTypes } from 'libs/end-to-end/src/data-rules';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { IconGreaterThanOrEqualToSymbolSvg } from 'apps/sigma2-ui/src/components/svg/IconGreaterThanOrEqualToSymbol';
import { IconLessThanOrEqualToSymbolSvg } from 'apps/sigma2-ui/src/components/svg/IconLessThanOrEqualToSymbol';
import { IconCaseSensitiveSymbolSvg } from 'apps/sigma2-ui/src/components/svg/IconCaseSensitiveSymbol';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';

export type PatternBuilderPendingSegmentState = {
  segment: DataRulePatternSegment;
  index: number;
} | null;

export interface PatternBuilderCallbacks {
  onApplyPatternChanges: () => void;
  onDiscardPatternChanges: () => void;
  onRemoveSegment: (index: number) => void;
  onAddSegment: () => void;
  onApplySegmentChanges: () => void;
  onDiscardSegmentChanges: () => void;
  onAddTextToSegmentText: (params: {
    text: string;
    isCaseSensitive: boolean;
  }) => void;
  onAddExcludingTextToSegmentWildcardString: (params: {
    text: string;
    isCaseSensitive: boolean;
  }) => void;
  onCaseSensitivityChangeForSegmentWildcardStringExcludingModeCharacters: (
    newValue: boolean,
  ) => void;
  onCharactersChangeForSegmentWildcardStringExcludingModeCharacters: (
    newValue: string,
  ) => void;
  onRemoveExcludingTextFromSegmentWildcardString: (index: number) => void;
  onRemoveTextFromSegmentText: (index: number) => void;
  onChangeSegmentType: (newType: DataRulePatternSegmentType) => void;
  onEditSegment: (index: number) => void;
  onExcludingModeChangeForSegmentWildcardString: (
    newValue: DataRulePatternSegmentWildcardStringExcludingMode,
  ) => void;
  onMinimumChangeForSegmentWildcardNumber: (
    newValue: number | undefined,
  ) => void;
  onMaximumChangeForSegmentWildcardNumber: (
    newValue: number | undefined,
  ) => void;
  onAllowNanAndInfinityChangeForSegmentWildcardNumber: (
    newValue: boolean,
  ) => void;
  onMinimumChangeForSegmentWildcardDate: (newValue: string | undefined) => void;
  onMaximumChangeForSegmentWildcardDate: (newValue: string | undefined) => void;
}

export interface PatternBuilderProps {
  pattern: DataRulePattern;
  pendingSegmentIndex: number;
  patternBuilderCallbacks: PatternBuilderCallbacks;
}

export interface PatternSegmentRemoveButtonProps {
  onRemove: () => void;
  tooltipTranslatedContent: TranslatedContent;
}

export const PatternSegmentRemoveButton: React.FC<
  PatternSegmentRemoveButtonProps
> = ({ onRemove, tooltipTranslatedContent }) => {
  return (
    <SvgBasedButton
      svgFn={IconTrashCanSvg}
      tooltipTranslatedContent={tooltipTranslatedContent}
      textColorClass={'text-red-500'}
      hoverTextColorClass={'text-red-700'}
      selectedTextColorClass={''}
      isSelected={false}
      isDisabled={false}
      onClick={onRemove}
    />
  );
};

export interface PatternSegmentEditButtonProps {
  onClick: () => void;
}

const SvgBasedButton: React.FC<{
  svgFn: React.FC<SvgProps>;
  tooltipTranslatedContent: TranslatedContent;
  textColorClass: string;
  hoverTextColorClass: string;
  selectedTextColorClass: string;
  isSelected: boolean;
  isDisabled: boolean;
  onClick: () => void;
}> = ({
  svgFn,
  isDisabled,
  tooltipTranslatedContent,
  hoverTextColorClass,
  textColorClass,
  onClick,
  isSelected,
  selectedTextColorClass,
}) => {
  return (
    <TouchTarget
      content={({ hovered }) => {
        return (
          <span
            className={cn('p-2 rounded-md inline-block', {
              'bg-white-200': !isDisabled && !hovered,
              'bg-royal-100': !isDisabled && hovered,
              [textColorClass]: !isDisabled && !hovered,
              [hoverTextColorClass]: !isDisabled && hovered,
              [selectedTextColorClass]: isSelected,
              'text-gray-500': isDisabled,
            })}
            title={getTranslatedContentAsString(tooltipTranslatedContent)}
          >
            {svgFn({
              svg: {
                className: 'w-4 h-4',
              },
            })}
          </span>
        );
      }}
      disabled={isDisabled}
      behavior={{
        type: 'onClick',
        onClick: onClick,
      }}
      className={'inline-block align-middle ml-2 leading-[0px] text-[0px]'}
    />
  );
};

export const PatternSegmentEditButton: React.FC<
  PatternSegmentEditButtonProps
> = ({ onClick }) => {
  const language = useLanguage();

  return (
    <SvgBasedButton
      svgFn={IconEditSvg}
      tooltipTranslatedContent={language.editPatternSegmentTooltip()}
      hoverTextColorClass={'text-gray-700'}
      textColorClass={'text-gray-500'}
      isDisabled={false}
      onClick={onClick}
      selectedTextColorClass={''}
      isSelected={false}
    />
  );
};

export interface PatternTextAddButtonProps {
  onAdd: () => void;
  isDisabled: boolean;
  tooltipTranslatedContent: TranslatedContent;
}

export const PatternTextAddButton: React.FC<PatternTextAddButtonProps> = ({
  onAdd,
  isDisabled,
  tooltipTranslatedContent,
}) => {
  return (
    <SvgBasedButton
      svgFn={IconAddCircleOutlineSvg}
      tooltipTranslatedContent={tooltipTranslatedContent}
      textColorClass={'text-green-500'}
      hoverTextColorClass={'text-green-700'}
      isDisabled={isDisabled}
      onClick={onAdd}
      selectedTextColorClass={''}
      isSelected={false}
    />
  );
};

export interface PatternSegmentSaveChangesButtonProps {
  onSave: () => void;
  isDisabled: boolean;
}

export const PatternSegmentSaveChangesButton: React.FC<
  PatternSegmentSaveChangesButtonProps
> = ({ onSave, isDisabled }) => {
  const language = useLanguage();

  return (
    <SvgBasedButton
      svgFn={IconCheckMark24pxSvg}
      tooltipTranslatedContent={language.segmentSaveChangesTooltip({
        isDisabled,
      })}
      textColorClass={'text-green-500'}
      hoverTextColorClass={'text-green-700'}
      isDisabled={isDisabled}
      onClick={onSave}
      selectedTextColorClass={''}
      isSelected={false}
    />
  );
};

export interface PatternSegmentDiscardChangesButtonProps {
  onDiscard: () => void;
}

export const PatternSegmentDiscardChangesButton: React.FC<
  PatternSegmentDiscardChangesButtonProps
> = ({ onDiscard }) => {
  const language = useLanguage();

  return (
    <SvgBasedButton
      svgFn={IconCloseSvg}
      tooltipTranslatedContent={language.segmentDiscardChangesTooltip()}
      textColorClass={'text-red-500'}
      hoverTextColorClass={'text-red-700'}
      isDisabled={false}
      onClick={onDiscard}
      selectedTextColorClass={''}
      isSelected={false}
    />
  );
};

export interface PatternSegmentNanAndInfinityButtonProps {
  allowNanAndInfinity: boolean;
  onUpdateAllowNanAndInfinity: (newValue: boolean) => void;
}

export const PatternSegmentNanAndInfinityButton: React.FC<
  PatternSegmentNanAndInfinityButtonProps
> = ({ allowNanAndInfinity, onUpdateAllowNanAndInfinity }) => {
  const language = useLanguage();

  return (
    <SvgBasedButton
      svgFn={IconInfinitySymbolSvg}
      tooltipTranslatedContent={language.nanAndInfinityToggleTooltip({
        allowNanAndInfinity,
      })}
      textColorClass={'text-gray-400'}
      hoverTextColorClass={'text-coral-500'}
      isDisabled={false}
      onClick={() => {
        onUpdateAllowNanAndInfinity(!allowNanAndInfinity);
      }}
      selectedTextColorClass={'text-royal-600'}
      isSelected={allowNanAndInfinity}
    />
  );
};

export interface PatternSegmentTextEditableProps
  extends PatternSegmentEditableBaseProps {
  segment: DataRulePatternSegmentText;
}

export const PatternSegmentTextEditable: React.FC<
  PatternSegmentTextEditableProps
> = ({
  segment,
  patternBuilderCallbacks: {
    onAddTextToSegmentText,
    onRemoveTextFromSegmentText,
    onApplySegmentChanges,
    onDiscardSegmentChanges,
  },
}) => {
  const language = useLanguage();

  const [pendingText, setPendingText] = React.useState<string>('');
  const [pendingIsCaseSensitive, setPendingIsCaseSensitive] =
    React.useState<boolean>(false);

  const canAddText = useMemo(() => {
    if (!pendingText) {
      return false;
    }

    const isDupe = segment.text.some(
      (text) =>
        text.text === pendingText &&
        text.isCaseSensitive === pendingIsCaseSensitive,
    );

    return !isDupe;
  }, [pendingText, pendingIsCaseSensitive, segment.text]);

  const canSaveSegmentChanges = useMemo(() => {
    if (segment.text.length === 0 || pendingText !== '') {
      return false;
    }

    return true;
  }, [pendingText, segment.text.length]);

  return (
    <>
      <InputWithCaseSensitivityCheckbox
        isCaseSensitive={pendingIsCaseSensitive}
        onCaseSensitivityChange={(newValue) => {
          setPendingIsCaseSensitive(newValue);
        }}
        placeholder={getTranslatedContentAsString(
          language.enterTextPlaceholder(),
        )}
        onChange={(newValue) => {
          setPendingText(newValue);
        }}
        value={pendingText}
      />
      <PatternTextAddButton
        isDisabled={!canAddText}
        onAdd={() => {
          onAddTextToSegmentText({
            text: pendingText,
            isCaseSensitive: pendingIsCaseSensitive,
          });
        }}
        tooltipTranslatedContent={language.addTextStringTooltip({
          isDisabled: !canAddText,
        })}
      />
      {segment.text.map((text, index) => {
        return (
          <React.Fragment key={`segment-text-fragment-${index}`}>
            <span
              key={`segment-text-${index}`}
              className={
                'inline-block bg-amber-200 p-2 rounded-md ml-2 leading-4'
              }
            >
              <S2Typography
                as={'span'}
                variant={'headline-7-alt'}
                translatedContent={language.patternTextPreview({
                  text: text.text,
                  isCaseSensitive: text.isCaseSensitive,
                })}
              />

              <PatternSegmentRemoveButton
                tooltipTranslatedContent={language.segmentRemoveTextItem({
                  text: text.text,
                  isCaseSensitive: text.isCaseSensitive,
                })}
                onRemove={() => {
                  onRemoveTextFromSegmentText(index);
                }}
              />
            </span>
          </React.Fragment>
        );
      })}
      <PatternSegmentSaveAndDiscardButtons
        onSaveSegment={onApplySegmentChanges}
        onDiscardSegmentChanges={onDiscardSegmentChanges}
        isSaveDisabled={!canSaveSegmentChanges}
      />
    </>
  );
};

export interface InputWithCaseSensitivityCheckboxProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  isCaseSensitive: boolean;
  onCaseSensitivityChange: (isCaseSensitive: boolean) => void;
  onEnter?: () => void;
}

export const InputWithCaseSensitivityCheckbox: React.FC<
  InputWithCaseSensitivityCheckboxProps
> = ({
  value,
  onChange,
  isCaseSensitive,
  onCaseSensitivityChange,
  onEnter,
  placeholder,
}) => {
  const language = useLanguage();

  return (
    <>
      <input
        type="text"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyUp={(event) => {
          if (event.key === 'Enter') {
            onEnter?.();
          }
        }}
        className={'form-input ml-2'}
        placeholder={placeholder}
      />
      <SvgBasedButton
        svgFn={IconCaseSensitiveSymbolSvg}
        tooltipTranslatedContent={language.caseSensitiveToggleTooltip({
          isCaseSensitive,
        })}
        textColorClass={'text-gray-400'}
        hoverTextColorClass={'text-coral-500'}
        selectedTextColorClass={'text-royal-600'}
        isSelected={isCaseSensitive}
        isDisabled={false}
        onClick={() => {
          onCaseSensitivityChange(!isCaseSensitive);
        }}
      />
    </>
  );
};

export interface PatternMinimumButtonProps {
  hasMinimum: boolean;
  onUpdateHasMinimum: (newHasMinimum: boolean) => void;
}

export const PatternMinimumButton: React.FC<PatternMinimumButtonProps> = ({
  hasMinimum,
  onUpdateHasMinimum,
}) => {
  const language = useLanguage();

  return (
    <SvgBasedButton
      svgFn={IconGreaterThanOrEqualToSymbolSvg}
      tooltipTranslatedContent={language.minimumToggleTooltip({
        hasMinimum,
      })}
      textColorClass={'text-gray-400'}
      hoverTextColorClass={'text-coral-500'}
      selectedTextColorClass={'text-royal-600'}
      isSelected={hasMinimum}
      isDisabled={false}
      onClick={() => {
        onUpdateHasMinimum(!hasMinimum);
      }}
    />
  );
};

export interface PatternMaximumButtonProps {
  hasMaximum: boolean;
  onUpdateHasMaximum: (newHasMaximum: boolean) => void;
}

export const PatternMaximumButton: React.FC<PatternMaximumButtonProps> = ({
  hasMaximum,
  onUpdateHasMaximum,
}) => {
  const language = useLanguage();

  return (
    <SvgBasedButton
      svgFn={IconLessThanOrEqualToSymbolSvg}
      tooltipTranslatedContent={language.maximumToggleTooltip({
        hasMaximum,
      })}
      textColorClass={'text-gray-400'}
      hoverTextColorClass={'text-coral-500'}
      selectedTextColorClass={'text-royal-600'}
      isSelected={hasMaximum}
      isDisabled={false}
      onClick={() => {
        onUpdateHasMaximum(!hasMaximum);
      }}
    />
  );
};

export interface PatternSegmentWildcardStringEditableProps
  extends PatternSegmentEditableBaseProps {
  segment: DataRulePatternSegmentWildcardString;
}

export const PatternSegmentWildcardStringEditable: React.FC<
  PatternSegmentWildcardStringEditableProps
> = ({
  segment,
  patternBuilderCallbacks: {
    onApplySegmentChanges,
    onDiscardSegmentChanges,
    onAddExcludingTextToSegmentWildcardString,
    onCaseSensitivityChangeForSegmentWildcardStringExcludingModeCharacters,
    onCharactersChangeForSegmentWildcardStringExcludingModeCharacters,
    onExcludingModeChangeForSegmentWildcardString,
    onRemoveExcludingTextFromSegmentWildcardString,
  },
}) => {
  const language = useLanguage();

  const [pendingText, setPendingText] = React.useState<string>('');
  const [pendingIsCaseSensitive, setPendingIsCaseSensitive] =
    React.useState<boolean>(false);

  const canAddExcludedText = useMemo(() => {
    if (!pendingText) {
      return false;
    }

    const isDupe = segment.excludingText.some(
      (text) =>
        text.text === pendingText &&
        text.isCaseSensitive === pendingIsCaseSensitive,
    );

    return !isDupe;
  }, [pendingText, pendingIsCaseSensitive, segment.excludingText]);

  const onAddExcludedText = useCallback(() => {
    if (!canAddExcludedText) {
      return;
    }

    onAddExcludingTextToSegmentWildcardString({
      text: pendingText,
      isCaseSensitive: pendingIsCaseSensitive,
    });
    setPendingText('');
  }, [
    canAddExcludedText,
    onAddExcludingTextToSegmentWildcardString,
    pendingText,
    pendingIsCaseSensitive,
  ]);

  const canSaveSegmentChanges = useMemo(() => {
    if (
      segment.excludingMode === 'characters' &&
      segment.excludingCharacters === ''
    ) {
      return false;
    }

    if (
      segment.excludingMode === 'text' &&
      segment.excludingText.length === 0
    ) {
      return false;
    }

    if (segment.excludingMode === 'text' && pendingText !== '') {
      return false;
    }

    return true;
  }, [
    pendingText,
    segment.excludingCharacters,
    segment.excludingMode,
    segment.excludingText.length,
  ]);

  const excludingComponent = useMemo(() => {
    if (segment.excludingMode === 'nothing') {
      return null;
    }

    if (segment.excludingMode === 'characters') {
      return (
        <InputWithCaseSensitivityCheckbox
          isCaseSensitive={segment.excludingCharactersIsCaseSensitive}
          onCaseSensitivityChange={(newValue) => {
            onCaseSensitivityChangeForSegmentWildcardStringExcludingModeCharacters(
              newValue,
            );
          }}
          placeholder={getTranslatedContentAsString(
            language.enterCharactersPlaceholder(),
          )}
          onChange={(newValue) => {
            onCharactersChangeForSegmentWildcardStringExcludingModeCharacters(
              newValue,
            );
          }}
          value={segment.excludingCharacters}
        />
      );
    }

    if (segment.excludingMode === 'text') {
      return (
        <>
          <InputWithCaseSensitivityCheckbox
            isCaseSensitive={pendingIsCaseSensitive}
            onCaseSensitivityChange={setPendingIsCaseSensitive}
            placeholder={getTranslatedContentAsString(
              language.enterTextPlaceholder(),
            )}
            onChange={(newValue) => {
              setPendingText(newValue);
            }}
            onEnter={() => {
              if (canAddExcludedText) {
                onAddExcludedText();
              }
            }}
            value={pendingText}
          />
          <PatternTextAddButton
            isDisabled={!canAddExcludedText}
            onAdd={onAddExcludedText}
            tooltipTranslatedContent={language.addTextStringTooltip({
              isDisabled: !canAddExcludedText,
            })}
          />
          {segment.excludingText.map((text, index) => {
            return (
              <React.Fragment key={`excluding-text-fragment-${index}`}>
                <span
                  key={`excluding-text-${index}`}
                  className={
                    'inline-block bg-amber-200 p-2 rounded-md ml-2 leading-4'
                  }
                >
                  <S2Typography
                    as={'span'}
                    variant={'headline-7-alt'}
                    translatedContent={language.patternExcludedText({
                      text: text.text,
                      isCaseSensitive: text.isCaseSensitive,
                    })}
                  />

                  <PatternSegmentRemoveButton
                    tooltipTranslatedContent={language.segmentRemoveTextItem({
                      text: text.text,
                      isCaseSensitive: text.isCaseSensitive,
                    })}
                    onRemove={() => {
                      onRemoveExcludingTextFromSegmentWildcardString(index);
                    }}
                  />
                </span>
              </React.Fragment>
            );
          })}
        </>
      );
    }

    return null;
  }, [
    segment.excludingMode,
    segment.excludingCharactersIsCaseSensitive,
    segment.excludingCharacters,
    segment.excludingText,
    language,
    onCaseSensitivityChangeForSegmentWildcardStringExcludingModeCharacters,
    onCharactersChangeForSegmentWildcardStringExcludingModeCharacters,
    pendingIsCaseSensitive,
    pendingText,
    canAddExcludedText,
    onAddExcludedText,
    onRemoveExcludingTextFromSegmentWildcardString,
  ]);

  return (
    <>
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.withLabel()}
      />
      <SimpleInlineDropdown
        currentValue={segment.excludingMode}
        onChange={(newValue) => {
          if (segment.excludingMode === newValue) {
            return;
          }

          onExcludingModeChangeForSegmentWildcardString(newValue);
        }}
        options={DataRulePatternSegmentWildcardStringExcludingModes.map(
          (m) => m,
        )}
        renderFn={(mode) => language[`${mode}PatternSegmentWildcardLabel`]()}
      />
      {excludingComponent}
      <PatternSegmentSaveAndDiscardButtons
        onSaveSegment={onApplySegmentChanges}
        onDiscardSegmentChanges={onDiscardSegmentChanges}
        isSaveDisabled={!canSaveSegmentChanges}
      />
    </>
  );
};

export interface PatternSegmentNumberInputProps {
  value: number | undefined;
  onUpdate: (newValue: number | undefined) => void;
  placeholder: string;
  showErrorHint: boolean;
}

const toWorkingValue = (current: number | undefined): string => {
  return current === undefined ? '' : current.toString(10);
};

export const PatternSegmentNumberInput: React.FC<
  PatternSegmentNumberInputProps
> = ({ value, onUpdate, placeholder, showErrorHint }) => {
  const [workingValue, setWorkingValue] = React.useState(toWorkingValue(value));

  const workingValueAsNumber = useMemo(() => {
    try {
      return parseFloat(workingValue);
    } catch {
      return undefined;
    }
  }, [workingValue]);

  return (
    <input
      type="text"
      value={workingValue}
      className={cn('form-input ml-2', {
        'border-red-500': showErrorHint,
      })}
      onFocus={() => {
        const newWorkingValue = toWorkingValue(value);

        if (newWorkingValue !== workingValue) {
          setWorkingValue(newWorkingValue);
        }
      }}
      onBlur={() => {
        if (workingValue.trim() === '' && value !== undefined) {
          onUpdate(undefined);
        } else if (
          workingValueAsNumber !== undefined &&
          !isNaN(workingValueAsNumber) &&
          isFinite(workingValueAsNumber)
        ) {
          onUpdate(workingValueAsNumber);
        } else {
          // Set the value back, since it is now invalid.
          setWorkingValue(toWorkingValue(value));
        }
      }}
      onChange={(event) => {
        setWorkingValue(event.target.value);
      }}
      placeholder={placeholder}
    />
  );
};

// These are in the format YYYY-MM-DD, which is the only format we support for now.
export interface PatternSegmentDateInputProps {
  value: string | undefined;
  onUpdate: (newValue: string | undefined) => void;
  showErrorHint: boolean;
}

export const PatternSegmentDateInput: React.FC<
  PatternSegmentDateInputProps
> = ({ value, onUpdate, showErrorHint }) => {
  return (
    <input
      type="date"
      value={value}
      className={cn({
        'form-input border-red-500': showErrorHint,
      })}
      onChange={(event) => {
        const newValue = event.target.value;
        if (newValue === '') {
          onUpdate(undefined);
        } else {
          onUpdate(newValue);
        }
      }}
    />
  );
};

export interface PatternSegmentWildcardNumberEditableProps
  extends PatternSegmentEditableBaseProps {
  segment: DataRulePatternSegmentWildcardNumber;
}

export const PatternSegmentWildcardNumberEditable: React.FC<
  PatternSegmentWildcardNumberEditableProps
> = ({
  segment,
  patternBuilderCallbacks: {
    onApplySegmentChanges,
    onDiscardSegmentChanges,
    onMinimumChangeForSegmentWildcardNumber,
    onMaximumChangeForSegmentWildcardNumber,
    onAllowNanAndInfinityChangeForSegmentWildcardNumber,
  },
}) => {
  const canSaveSegmentChanges = useMemo(() => {
    if (segment.minimum !== undefined && segment.maximum !== undefined) {
      return segment.minimum <= segment.maximum;
    }

    return true;
  }, [segment.minimum, segment.maximum]);

  return (
    <>
      <PatternMinimumButton
        hasMinimum={segment.minimum !== undefined}
        onUpdateHasMinimum={(newHasMinimum) => {
          const currentHasMinimum = segment.minimum !== undefined;
          if (currentHasMinimum !== newHasMinimum) {
            onMinimumChangeForSegmentWildcardNumber(
              newHasMinimum ? 0 : undefined,
            );
          }
        }}
      />
      {segment.minimum !== undefined && (
        <PatternSegmentNumberInput
          value={segment.minimum}
          showErrorHint={!canSaveSegmentChanges}
          onUpdate={(newMinimum) => {
            onMinimumChangeForSegmentWildcardNumber(newMinimum);
          }}
          placeholder={''}
        />
      )}
      <PatternMaximumButton
        hasMaximum={segment.maximum !== undefined}
        onUpdateHasMaximum={(newHasMaximum) => {
          const currentHasMaximum = segment.maximum !== undefined;
          if (currentHasMaximum !== newHasMaximum) {
            onMaximumChangeForSegmentWildcardNumber(
              newHasMaximum ? 100 : undefined,
            );
          }
        }}
      />
      {segment.maximum !== undefined && (
        <PatternSegmentNumberInput
          value={segment.maximum}
          showErrorHint={!canSaveSegmentChanges}
          onUpdate={(newMaximum) => {
            onMaximumChangeForSegmentWildcardNumber(newMaximum);
          }}
          placeholder={''}
        />
      )}
      <PatternSegmentNanAndInfinityButton
        allowNanAndInfinity={segment.allowNanAndInfinity}
        onUpdateAllowNanAndInfinity={(newValue) => {
          onAllowNanAndInfinityChangeForSegmentWildcardNumber(newValue);
        }}
      />
      <PatternSegmentSaveAndDiscardButtons
        onSaveSegment={onApplySegmentChanges}
        onDiscardSegmentChanges={onDiscardSegmentChanges}
        isSaveDisabled={!canSaveSegmentChanges}
      />
    </>
  );
};

export interface PatternSegmentWildcardDateEditableProps
  extends PatternSegmentEditableBaseProps {
  segment: DataRulePatternSegmentWildcardDate;
}

export const PatternSegmentWildcardDateEditable: React.FC<
  PatternSegmentWildcardDateEditableProps
> = ({
  segment,
  patternBuilderCallbacks: {
    onApplySegmentChanges,
    onDiscardSegmentChanges,
    onMinimumChangeForSegmentWildcardDate,
    onMaximumChangeForSegmentWildcardDate,
  },
}) => {
  // This works because dates are currently in the format YYYY-MM-DD.
  const canSaveSegmentChanges = useMemo(() => {
    if (segment.minimum !== undefined && segment.maximum !== undefined) {
      return segment.minimum <= segment.maximum;
    }

    return true;
  }, [segment.minimum, segment.maximum]);

  return (
    <>
      <PatternMinimumButton
        hasMinimum={segment.minimum !== undefined}
        onUpdateHasMinimum={(newHasMinimum) => {
          const currentHasMinimum = segment.minimum !== undefined;
          if (currentHasMinimum !== newHasMinimum) {
            onMinimumChangeForSegmentWildcardDate(
              newHasMinimum ? '2025-01-01' : undefined,
            );
          }
        }}
      />
      {segment.minimum !== undefined && (
        <PatternSegmentDateInput
          value={segment.minimum}
          showErrorHint={!canSaveSegmentChanges}
          onUpdate={(newMinimum) => {
            onMinimumChangeForSegmentWildcardDate(newMinimum);
          }}
        />
      )}
      <PatternMaximumButton
        hasMaximum={segment.maximum !== undefined}
        onUpdateHasMaximum={(newHasMaximum) => {
          const currentHasMaximum = segment.maximum !== undefined;
          if (currentHasMaximum !== newHasMaximum) {
            onMaximumChangeForSegmentWildcardDate(
              newHasMaximum ? '2025-12-31' : undefined,
            );
          }
        }}
      />
      {segment.maximum !== undefined && (
        <PatternSegmentDateInput
          value={segment.maximum}
          showErrorHint={!canSaveSegmentChanges}
          onUpdate={(newMaximum) => {
            onMaximumChangeForSegmentWildcardDate(newMaximum);
          }}
        />
      )}
      <PatternSegmentSaveAndDiscardButtons
        onSaveSegment={onApplySegmentChanges}
        onDiscardSegmentChanges={onDiscardSegmentChanges}
        isSaveDisabled={!canSaveSegmentChanges}
      />
    </>
  );
};

export interface PatternSegmentWhitespaceEditableProps
  extends PatternSegmentEditableBaseProps {
  segment: DataRulePatternSegmentWhitespace;
}

export const PatternSegmentWhitespaceEditable: React.FC<
  PatternSegmentWhitespaceEditableProps
> = ({
  patternBuilderCallbacks: { onApplySegmentChanges, onDiscardSegmentChanges },
}) => {
  return (
    <>
      <PatternSegmentSaveAndDiscardButtons
        onSaveSegment={onApplySegmentChanges}
        onDiscardSegmentChanges={onDiscardSegmentChanges}
        isSaveDisabled={false}
      />
    </>
  );
};

export interface PatternSegmentTextReadOnlyProps {
  segment: DataRulePatternSegmentText;
}

export const PatternSegmentTextReadOnly: React.FC<
  PatternSegmentTextReadOnlyProps
> = ({ segment }) => {
  const language = useLanguage();

  return (
    <>
      {segment.text.map((text, index) => {
        return (
          <React.Fragment key={`segment-text-readonly-fragment-${index}`}>
            {index !== 0 && (
              <S2Typography
                as={'span'}
                variant={'headline-7-alt'}
                translatedContent={language.space()}
              />
            )}
            <span
              key={`segment-text-${index}`}
              className={
                'bg-amber-200 p-2 rounded-md inline-block ml-2 leading-4'
              }
            >
              <S2Typography
                as={'span'}
                variant={'headline-7-alt'}
                translatedContent={language.patternExcludedText({
                  text: text.text,
                  isCaseSensitive: text.isCaseSensitive,
                })}
              />
            </span>
          </React.Fragment>
        );
      })}
    </>
  );
};

export interface PatternSegmentWildcardNumberReadOnlyProps {
  segment: DataRulePatternSegmentWildcardNumber;
}

export const PatternSegmentWildcardNumberReadOnly: React.FC<
  PatternSegmentWildcardNumberReadOnlyProps
> = ({ segment }) => {
  const language = useLanguage();

  return (
    <>
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.numberWildcardPreview({
          minimum: segment.minimum,
          maximum: segment.maximum,
          allowNanAndInfinity: segment.allowNanAndInfinity,
        })}
      />
    </>
  );
};

export interface PatternSegmentWildcardDateReadOnlyProps {
  segment: DataRulePatternSegmentWildcardDate;
}

export const PatternSegmentWildcardDateReadOnly: React.FC<
  PatternSegmentWildcardDateReadOnlyProps
> = ({ segment }) => {
  const language = useLanguage();

  return (
    <>
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.dateWildcardPreview({
          minimum: segment.minimum,
          maximum: segment.maximum,
        })}
      />
    </>
  );
};

export interface PatternSegmentWildcardStringReadOnlyProps {
  segment: DataRulePatternSegmentWildcardString;
}

export const PatternSegmentWildcardStringReadOnly: React.FC<
  PatternSegmentWildcardStringReadOnlyProps
> = ({ segment }) => {
  const language = useLanguage();

  const excludingComponent = useMemo(() => {
    if (segment.excludingMode === 'nothing') {
      return null;
    }

    if (segment.excludingMode === 'characters') {
      return (
        <S2Typography
          as={'span'}
          variant={'headline-7-alt'}
          translatedContent={language.patternTextPreview({
            text: segment.excludingCharacters,
            isCaseSensitive: segment.excludingCharactersIsCaseSensitive,
          })}
        />
      );
    }

    if (segment.excludingMode === 'text') {
      return (
        <>
          {segment.excludingText.map((text, index) => {
            return (
              <React.Fragment key={`excluding-text-readonly-fragment-${index}`}>
                {index !== 0 && (
                  <S2Typography
                    as={'span'}
                    variant={'headline-7-alt'}
                    translatedContent={language.space()}
                  />
                )}
                <span
                  key={`excluding-text-${index}`}
                  className={
                    'bg-amber-200 p-2 rounded-md inline-block ml-2 leading-4'
                  }
                >
                  <S2Typography
                    as={'span'}
                    variant={'headline-7-alt'}
                    translatedContent={language.patternExcludedText({
                      text: text.text,
                      isCaseSensitive: text.isCaseSensitive,
                    })}
                  />
                </span>
              </React.Fragment>
            );
          })}
        </>
      );
    }

    return null;
  }, [language, segment]);

  return (
    <>
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.withLabel()}
      />
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.space()}
      />

      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language[
          `${segment.excludingMode}PatternSegmentWildcardLabel`
        ]()}
      />
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.space()}
      />
      {excludingComponent}
    </>
  );
};

interface PatternSegmentEditableBaseProps {
  patternBuilderCallbacks: PatternBuilderCallbacks;
}

export interface PatternSegmentEditableProps
  extends PatternSegmentEditableBaseProps {
  segment: DataRulePatternSegment;
  segmentNumber: number;
}

export interface PatternSegmentSaveAndDiscardButtonsProps {
  isSaveDisabled: boolean;
  onDiscardSegmentChanges: () => void;
  onSaveSegment: () => void;
}

export const PatternSegmentSaveAndDiscardButtons: React.FC<
  PatternSegmentSaveAndDiscardButtonsProps
> = ({ isSaveDisabled, onSaveSegment, onDiscardSegmentChanges }) => {
  return (
    <>
      <PatternSegmentSaveChangesButton
        onSave={onSaveSegment}
        isDisabled={isSaveDisabled}
      />
      <PatternSegmentDiscardChangesButton onDiscard={onDiscardSegmentChanges} />
    </>
  );
};

export const PatternSegmentEditable: React.FC<PatternSegmentEditableProps> = ({
  segment,
  segmentNumber,
  patternBuilderCallbacks,
}) => {
  const language = useLanguage();

  const segmentComponent = useMemo(() => {
    switch (segment.type) {
      case 'text':
        return (
          <PatternSegmentTextEditable
            segment={segment}
            patternBuilderCallbacks={patternBuilderCallbacks}
          />
        );

      case 'wildcard-string':
        return (
          <PatternSegmentWildcardStringEditable
            segment={segment}
            patternBuilderCallbacks={patternBuilderCallbacks}
          />
        );

      case 'wildcard-number':
        return (
          <PatternSegmentWildcardNumberEditable
            segment={segment}
            patternBuilderCallbacks={patternBuilderCallbacks}
          />
        );
      case 'wildcard-date':
        return (
          <PatternSegmentWildcardDateEditable
            segment={segment}
            patternBuilderCallbacks={patternBuilderCallbacks}
          />
        );

      case 'whitespace':
        return (
          <PatternSegmentWhitespaceEditable
            segment={segment}
            patternBuilderCallbacks={patternBuilderCallbacks}
          />
        );

      default:
        return <span />;
    }
  }, [patternBuilderCallbacks, segment]);

  return (
    <>
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={
          segmentNumber === 1
            ? language.firstSegmentLabel()
            : language.nextSegmentLabel()
        }
      />
      <SimpleInlineDropdown
        currentValue={segment.type}
        onChange={(newSegmentType) => {
          patternBuilderCallbacks.onChangeSegmentType(newSegmentType);
        }}
        options={DataRulePatternSegmentTypes.map((t) => t)}
        renderFn={(segmentType) => {
          if (segmentType === 'text') {
            return language.textSegmentLabel();
          } else if (segmentType === 'wildcard-string') {
            return language.wildcardStringSegmentLabel();
          } else if (segmentType === 'wildcard-number') {
            return language.wildcardNumberSegmentLabel();
          } else if (segmentType === 'wildcard-date') {
            return language.wildcardDateSegmentLabel();
          } else if (segmentType === 'whitespace') {
            return language.whitespaceSegmentLabel();
          } else {
            throw guardExhaustive(segmentType);
          }
        }}
      />
      {segmentComponent}
    </>
  );
};

const convertSegmentTypeToTranslatedContent = ({
  segmentType,
  language,
}: {
  segmentType: DataRulePatternSegmentType;
  language: Phrases;
}): TranslatedContent => {
  if (segmentType === 'text') {
    return language.textSegmentLabel();
  } else if (segmentType === 'wildcard-string') {
    return language.wildcardStringSegmentLabel();
  } else if (segmentType === 'wildcard-number') {
    return language.wildcardNumberSegmentLabel();
  } else if (segmentType === 'wildcard-date') {
    return language.wildcardDateSegmentLabel();
  } else if (segmentType === 'whitespace') {
    return language.whitespaceSegmentLabel();
  } else {
    throw guardExhaustive(segmentType);
  }
};

export interface PatternSegmentReadOnlyProps {
  segment: DataRulePatternSegment;
  segmentNumber: number;
}

export const PatternSegmentReadOnly: React.FC<PatternSegmentReadOnlyProps> = ({
  segment,
  segmentNumber,
}) => {
  const language = useLanguage();

  const segmentComponent = useMemo(() => {
    switch (segment.type) {
      case 'text':
        return <PatternSegmentTextReadOnly segment={segment} />;

      case 'wildcard-string':
        return <PatternSegmentWildcardStringReadOnly segment={segment} />;

      case 'wildcard-number':
        return <PatternSegmentWildcardNumberReadOnly segment={segment} />;

      case 'wildcard-date':
        return <PatternSegmentWildcardDateReadOnly segment={segment} />;

      default:
        return <span />;
    }
  }, [segment]);

  return (
    <>
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={
          segmentNumber === 1
            ? language.firstSegmentLabel()
            : language.nextSegmentLabel()
        }
      />
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.space()}
      />
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={convertSegmentTypeToTranslatedContent({
          segmentType: segment.type,
          language,
        })}
      />
      <S2Typography
        as={'span'}
        variant={'headline-7-alt'}
        translatedContent={language.space()}
      />
      {segmentComponent}
    </>
  );
};

export interface PatternSegmentAddButtonProps {
  onAdd: () => void;
  isDisabled: boolean;
}

export const PatternSegmentAddButton: React.FC<
  PatternSegmentAddButtonProps
> = ({ onAdd, isDisabled }) => {
  const language = useLanguage();

  return (
    <TouchTarget
      content={({ hovered }) => {
        return (
          <span
            className={
              'flex flex-col justify-center h-12 p-2 rounded-b-2xl border-2'
            }
          >
            <S2Typography
              as={'span'}
              variant={
                isDisabled ? 'headline-7-alt' : 'headline-7-alt-underline'
              }
              translatedContent={language.addSegmentLabel()}
              textColorVariant={
                isDisabled ? 'gray-400' : hovered ? 'royal-700' : 'royal-600'
              }
            />
          </span>
        );
      }}
      disabled={isDisabled}
      behavior={{
        type: 'onClick',
        onClick: onAdd,
      }}
      className={'inline-block'}
    />
  );
};

export const PatternSegmentStartCap: React.FC = () => {
  const language = useLanguage();

  return (
    <span
      className={
        'p-2 rounded-t-2xl border-2 flex flex-col justify-center h-12 border-b-0'
      }
    >
      <S2Typography
        as={'span'}
        variant="headline-7-alt"
        translatedContent={language.patternLabel()}
      />
    </span>
  );
};

export const PatternSegmentEndCap: React.FC = () => {
  const language = useLanguage();

  return (
    <span
      className={'p-2 border-2 flex flex-col justify-center h-12 border-b-0'}
    >
      <S2Typography
        as={'span'}
        variant="headline-7-alt"
        translatedContent={language.endPatternLabel()}
      />
    </span>
  );
};

export const PatternBuilder: React.FC<PatternBuilderProps> = ({
  pattern,
  pendingSegmentIndex,
  patternBuilderCallbacks,
}) => {
  const language = useLanguage();

  const canAddSegment = useMemo(() => {
    return pendingSegmentIndex === -1;
  }, [pendingSegmentIndex]);

  const segmentComponents = useMemo(() => {
    const maxIndex = Math.max(pattern.length - 1, pendingSegmentIndex);
    const components: React.ReactNode[] = [];

    for (let segmentIndex = 0; segmentIndex <= maxIndex; segmentIndex++) {
      if (pendingSegmentIndex === segmentIndex) {
        components.push(
          <>
            <PatternSegmentEditable
              key={segmentIndex}
              segment={pattern[segmentIndex]}
              patternBuilderCallbacks={patternBuilderCallbacks}
              segmentNumber={segmentIndex + 1}
            />
          </>,
        );
      } else {
        components.push(
          <>
            <PatternSegmentReadOnly
              key={segmentIndex}
              segment={pattern[segmentIndex]}
              segmentNumber={segmentIndex + 1}
            />
            {pendingSegmentIndex === -1 && (
              <>
                <PatternSegmentEditButton
                  onClick={() => {
                    patternBuilderCallbacks.onEditSegment(segmentIndex);
                  }}
                />
                <PatternSegmentRemoveButton
                  tooltipTranslatedContent={language.removePatternSegmentTooltip()}
                  onRemove={() =>
                    patternBuilderCallbacks.onRemoveSegment(segmentIndex)
                  }
                />
              </>
            )}
          </>,
        );
      }
    }
    return components;
  }, [pattern, pendingSegmentIndex, patternBuilderCallbacks, language]);

  const patternCompilationResult = useMemo(() => {
    return compileDataRulePattern(pattern);
  }, [pattern]);

  const errorMessage: TranslatedContent | null = useMemo(() => {
    if (patternCompilationResult.resultType === 'ok') {
      return null;
    }

    if (
      patternCompilationResult.error.errorType ===
      'CompileDataRulePatternSequenceEmptyError'
    ) {
      // We don't show an error here, since it doesn't feel right and the UI will hint that they need more info.
      return null;
    }

    if (
      patternCompilationResult.error.errorType ===
      'CompileDataRulePatternSequenceOfWildcardsError'
    ) {
      return language.multipleWildcardsInARowError();
    }

    return language.unknownPatternError();
  }, [patternCompilationResult, language]);

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <div className={'flex flex-col'}>
        <PatternSegmentStartCap />
        {segmentComponents.map((segmentComponent, index) => {
          return (
            <span
              key={`segment-component-${index}`}
              className={cn(
                'leading-[3.5rem] p-2 min-h-12 border-2 border-b-0 gap-2',
                {
                  // This makes the row red if it is an error that is resulting from having more than one wildcard-type segment in a row.
                  'bg-red-200':
                    patternCompilationResult.resultType === 'err' &&
                    patternCompilationResult.error.errorType ===
                      'CompileDataRulePatternSequenceOfWildcardsError' &&
                    patternCompilationResult.error.invalidSegmentIndexes.includes(
                      index,
                    ),
                },
              )}
            >
              {segmentComponent}
            </span>
          );
        })}
        {pattern.length > 0 && <PatternSegmentEndCap />}
        <PatternSegmentAddButton
          onAdd={patternBuilderCallbacks.onAddSegment}
          isDisabled={!canAddSegment}
        />
      </div>
      {errorMessage && (
        <div className={'flex flex-row items-center'}>
          <S2Typography
            as={'span'}
            variant={'headline-7-alt'}
            translatedContent={errorMessage}
            textColorVariant={'red-600'}
          />
        </div>
      )}
      {pendingSegmentIndex === -1 &&
        patternCompilationResult.resultType === 'ok' && (
          <div className={'flex flex-row items-center'}>
            <S2Typography
              as={'span'}
              variant={'headline-7-alt'}
              translatedContent={language.patternPreview({
                sampleValues: patternCompilationResult.value.sampleValues,
              })}
            />
          </div>
        )}
    </Spacing>
  );
};

export interface SimpleInlineDropdownProps<T extends string> {
  currentValue: T;
  onChange: (value: T) => void;
  options: T[];
  renderFn: (value: T) => TranslatedContent;
}

export const SimpleInlineDropdown = <T extends string>({
  currentValue,
  onChange,
  options,
  renderFn,
}: SimpleInlineDropdownProps<T>) => {
  const [isOpen, toggleOpen] = useToggle({ initialState: false });

  return (
    <div className={'inline-block ml-2'}>
      <Spacing direction="horizontal" size="sm">
        <div className="relative">
          <button
            data-testid="simple-inline-dropdown-button text-royal-600"
            onClick={toggleOpen}
          >
            <Spacing direction="horizontal" size="xs">
              <S2Typography
                as={'span'}
                variant="headline-7-alt"
                translatedContent={renderFn(currentValue)}
                textColorVariant={'royal-600'}
              />

              <IconChevronRightSvg
                svg={{
                  className: cn('w-6 h-6 text-royal-600', {
                    'rotate-90': !isOpen,
                    '-rotate-90': isOpen,
                  }),
                }}
              />
            </Spacing>
          </button>

          <div className={cn('w-full border-t-2 -mt-1 border-royal-600')} />

          {isOpen && (
            <Spacing
              direction="vertical"
              size="gap-0"
              className={
                'z-50 absolute left-2 top-[3.5rem] max-h-96 h-fit w-max bg-white shadow-4px min-w-44 leading-6'
              }
            >
              {options.map((optionValue) => {
                const isSelected = optionValue === currentValue;

                return (
                  <button
                    data-testid={`simple-inline-dropdown-button-${optionValue}`}
                    key={`dropdown-option-${optionValue}`}
                    onClick={() => {
                      onChange(optionValue);
                      toggleOpen();
                    }}
                    className={cn('p-2 text-left w-full', {
                      'bg-royal-100': isSelected,
                      'bg-white hover:bg-royal-100': !isSelected,
                    })}
                  >
                    {convertTranslatedContentToReactNode(renderFn(optionValue))}
                  </button>
                );
              })}
            </Spacing>
          )}
        </div>
      </Spacing>
    </div>
  );
};
